import {
  EDIT_PERMISSIONS,
  EDIT_PERMISSION_ORDER,
  PERMISSION_TYPES,
  VIEW_PERMISSIONS,
  VIEW_PERMISSION_ORDER,
} from '../constants/dashboardBuilderConstants';
import {
  CREATE_PERMISSION_SHORT_LABELS,
  EDIT_PERMISSION_SHORT_LABELS,
  VIEW_PERMISSION_SHORT_LABELS,
} from '../constants/dashboardBuilderCopy';

const SEPARATOR = ' · ';

export const normalizeViewPermissions = (permissions) => {
  const filtered = (Array.isArray(permissions) ? permissions : []).filter(
    (key) => key === VIEW_PERMISSIONS.VIEW_ONLY || key === VIEW_PERMISSIONS.EXPORT_DATA
  );
  return Array.from(new Set([VIEW_PERMISSIONS.VIEW_ONLY, ...filtered]));
};

const isNormalizedShareAssignment = (entry) =>
  Boolean(
    entry &&
      typeof entry === 'object' &&
      (entry.permissionLevel === PERMISSION_TYPES.VIEW || entry.permissionLevel === PERMISSION_TYPES.EDIT) &&
      Array.isArray(entry.permissionModes) &&
      Array.isArray(entry.editPermissions) &&
      Array.isArray(entry.viewPermissions)
  );

export const formatCombinedPermissionsSummary = (assignment, options = {}) => {
  const maxVisible = options.maxVisible ?? 4;
  const normalized = isNormalizedShareAssignment(assignment)
    ? {
        ...assignment,
        viewPermissions: normalizeViewPermissions(assignment.viewPermissions),
      }
    : normalizeShareAssignment(assignment);
  const parts = [];

  if (normalized.permissionModes.includes(PERMISSION_TYPES.EDIT)) {
    const orderedEdit = EDIT_PERMISSION_ORDER.filter((key) => normalized.editPermissions.includes(key));
    orderedEdit.forEach((key) => {
      parts.push(EDIT_PERMISSION_SHORT_LABELS[key] || key);
    });
  }

  if (normalized.permissionModes.includes(PERMISSION_TYPES.VIEW)) {
    const orderedView = VIEW_PERMISSION_ORDER.filter((key) => normalized.viewPermissions.includes(key));
    orderedView.forEach((key) => {
      parts.push(VIEW_PERMISSION_SHORT_LABELS[key] || key);
    });
  }

  if (normalized.permissionModes.includes(PERMISSION_TYPES.CREATE)) {
    parts.push(CREATE_PERMISSION_SHORT_LABELS[EDIT_PERMISSIONS.ADD_WIDGET] || EDIT_PERMISSIONS.ADD_WIDGET);
  }

  if (parts.length === 0) {
    return { text: '—', fullList: [] };
  }

  const visible = parts.slice(0, maxVisible);
  const rest = parts.length - visible.length;
  const text = rest > 0 ? [...visible, `+${rest}`].join(SEPARATOR) : visible.join(SEPARATOR);

  return { text, fullList: parts };
};

/**
 * Normaliza un assignment legacy con `permissions: ['view'|'edit']` al nuevo shape.
 */
export const normalizeShareAssignment = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return {
      permissionLevel: PERMISSION_TYPES.VIEW,
      permissionModes: [PERMISSION_TYPES.VIEW],
      editPermissions: [],
      viewPermissions: [VIEW_PERMISSIONS.VIEW_ONLY],
    };
  }

  const isLegacyEdit = Array.isArray(entry.permissions) && entry.permissions.includes(PERMISSION_TYPES.EDIT);
  const rawModes = Array.isArray(entry.permissionModes)
    ? entry.permissionModes
    : isLegacyEdit
      ? [PERMISSION_TYPES.VIEW, PERMISSION_TYPES.EDIT]
      : [entry.permissionLevel || PERMISSION_TYPES.VIEW];

  const filteredModes = rawModes.filter(
    (mode) =>
      mode === PERMISSION_TYPES.VIEW || mode === PERMISSION_TYPES.EDIT || mode === PERMISSION_TYPES.CREATE
  );
  const uniqueModes = Array.from(new Set(filteredModes));
  const permissionModes = uniqueModes.includes(PERMISSION_TYPES.VIEW)
    ? uniqueModes
    : [PERMISSION_TYPES.VIEW, ...uniqueModes];

  const levelFromModes = permissionModes.includes(PERMISSION_TYPES.EDIT)
    ? PERMISSION_TYPES.EDIT
    : PERMISSION_TYPES.VIEW;

  if (entry.permissionLevel === PERMISSION_TYPES.VIEW || entry.permissionLevel === PERMISSION_TYPES.EDIT) {
    const resolvedCreateMode =
      permissionModes.includes(PERMISSION_TYPES.CREATE) ||
      (Array.isArray(entry.editPermissions) && entry.editPermissions.includes(EDIT_PERMISSIONS.ADD_WIDGET));
    return {
      permissionLevel: entry.permissionLevel || levelFromModes,
      permissionModes: resolvedCreateMode
        ? Array.from(new Set([...permissionModes, PERMISSION_TYPES.CREATE]))
        : permissionModes,
      editPermissions: Array.isArray(entry.editPermissions)
        ? entry.editPermissions.filter((key) => key !== EDIT_PERMISSIONS.ADD_WIDGET)
        : [],
      viewPermissions: normalizeViewPermissions(entry.viewPermissions),
    };
  }

  return {
    permissionLevel: levelFromModes,
    permissionModes,
    editPermissions: Array.isArray(entry.editPermissions)
      ? entry.editPermissions.filter((key) => key !== EDIT_PERMISSIONS.ADD_WIDGET)
      : [],
    viewPermissions: normalizeViewPermissions(entry.viewPermissions),
  };
};

/**
 * Payload para `sharedWith` del editor / API (campos estables).
 */
export const assignmentToSharedWithEntry = (assignment) => {
  const { permissionLevel, permissionModes, editPermissions, viewPermissions } =
    normalizeShareAssignment(assignment);
  const base = {
    type: assignment.type,
    id: assignment.id,
    name: assignment.name,
    permissionLevel,
    permissionModes,
    editPermissions,
    viewPermissions,
  };
  if (assignment.role != null) base.role = assignment.role;
  if (assignment.userCount != null) base.userCount = assignment.userCount;
  // Compatibilidad con mocks que aún lean `permissions`
  base.permissions = permissionModes.includes(PERMISSION_TYPES.EDIT)
    ? [PERMISSION_TYPES.VIEW, PERMISSION_TYPES.EDIT]
    : [PERMISSION_TYPES.VIEW];
  return base;
};
